import { useRef, useEffect } from 'react';
import { VideoProvider } from '../../../apis/api-client';
import { Box, CircularProgress, Typography } from '../../ui';
import WistiaPlayer from './WistiaPlayer';
import { useVideoWatchTracking } from '../../../hooks/useVideoWatchTracking';
import { useVideoSeekGuard } from '../../../hooks/useVideoSeekGuard';
import VideoWatchGuard from './VideoWatchGuard';

interface VideoViewerProps {
  lesson: any;
  playback?: {
    playbackId?: string;
    token?: string;
    status?: string;
    [key: string]: any;
  };
  lessonTitle: string;
  loading?: boolean;
  sx?: any;
  onVideoEnded?: () => void;
  // When true, onVideoEnded fires only once enough of the video has been
  // watched (prevents skipping ahead to claim completion).
  requireFullWatch?: boolean;
}

export default function VideoViewer({ lesson, playback, lessonTitle, loading, sx, onVideoEnded, requireFullWatch = true }: VideoViewerProps) {
  const playerRef = useRef<any>(null);

  useVideoWatchTracking(playerRef, {
    enabled: requireFullWatch,
    resetKey: lesson?.id,
    onComplete: () => onVideoEnded?.(),
  });

  // Anti-skip guard — learners only (admins keep requireFullWatch=false).
  const seekGuard = useVideoSeekGuard(playerRef, {
    enabled: requireFullWatch,
    resetKey: lesson?.id,
  });

  // Creators/admins bypass the full-watch requirement: simple 'ended' listener.
  useEffect(() => {
    if (requireFullWatch) return;
    const player = playerRef.current;
    if (!player || !onVideoEnded) return;

    const handleEnded = () => onVideoEnded();
    player.addEventListener('ended', handleEnded);
    return () => player.removeEventListener('ended', handleEnded);
  }, [onVideoEnded, requireFullWatch]);

  // Wistia branch — no Mux playback fetch needed
  if (lesson?.videoProvider === VideoProvider.Wistia) {
    return (
      <WistiaPlayer
        mediaId={lesson.externalVideoId}
        lessonTitle={lessonTitle}
        posterUrl={lesson.videoPosterUrl}
        onVideoEnded={onVideoEnded}
        requireFullWatch={requireFullWatch}
        sx={sx}
      />
    );
  }

  // Show loading spinner while fetching Mux video playback data
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, color: 'text.secondary', ...sx }}>
        <CircularProgress size={50} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading video...
        </Typography>
      </Box>
    );
  }

  if (!playback?.playbackId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', ...sx }}>
        {playback?.status === 'uploading' || playback?.status === 'processing'
          ? 'Video is being processed. Please check back in a few minutes.'
          : 'No video uploaded yet'}
      </Box>
    );
  }

  return (
    <VideoWatchGuard enabled={requireFullWatch} {...seekGuard}>
      <Box
        // Keying on lesson + playback id forces a fresh <mux-player> mount
        // whenever the learner switches lessons. The web component doesn't
        // reliably swap its internal media stream when playback-id changes
        // on a live element, so without this the old video keeps playing
        // under a new title.
        key={`${lesson?.id ?? 'none'}:${playback.playbackId}`}
        ref={playerRef}
        component="mux-player"
        playback-id={playback.playbackId}
        playback-token={playback.token}
        metadata-video-title={lessonTitle}
        accent-color="#1e64f0"
        sx={{
          width: '100%',
          height: '700px',
          '--media-object-fit': 'contain',
          '--media-object-position': 'center',
          ...sx,
        }}
      />
    </VideoWatchGuard>
  );
}
